/**
 * Admin Interview Detail Page
 *
 * Shows full interview details for a team member:
 * - Member info (name, team, role, email, status, reward)
 * - Interview metadata (date, interviewer)
 * - Structured Q&A responses
 * - Free-form researcher's summary
 * - AI-generated insights
 *
 * Route: /internal-interviews/:memberId
 */

import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  PencilSimple as Pencil,
  Plus,
  FloppyDisk as Save,
  Trash,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAdminInterviewDetail,
  useUpdateTeamMember,
} from "@/hooks/useInterviews";
import type {
  InterviewResponse,
  UpdateTeamMemberInput,
} from "@/services/interviews";
import { cn } from "@/utils/cn";

// ── Status badge variants ──────────────────────────────────

const STATUS_VARIANTS: Record<string, "default" | "outline"> = {
  Contacted: "outline",
  Scheduled: "outline",
  Interviewed: "default",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// ── Component ──────────────────────────────────────────────

export function AdminInterviewDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { data: member, isLoading, error } = useAdminInterviewDetail(memberId!);
  const updateMutation = useUpdateTeamMember();

  // ── Edit state ──
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    interviewDate: "",
    interviewer: "",
  });

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesForm, setNotesForm] = useState("");

  const [editingAiInsights, setEditingAiInsights] = useState(false);
  const [aiInsightsForm, setAiInsightsForm] = useState("");

  const [editingResponseIdx, setEditingResponseIdx] = useState<number | null>(
    null
  );
  const [responseForm, setResponseForm] = useState({
    question: "",
    answer: "",
  });

  const [addingResponse, setAddingResponse] = useState(false);
  const [newResponse, setNewResponse] = useState({
    question: "",
    answer: "",
  });

  // ── Handlers ──

  const save = useCallback(
    (input: UpdateTeamMemberInput) => {
      if (!memberId) return;
      updateMutation.mutate(
        { id: memberId, input },
        {
          onSuccess: () => {
            setEditingMeta(false);
            setEditingNotes(false);
            setEditingAiInsights(false);
            setEditingResponseIdx(null);
            setAddingResponse(false);
          },
        }
      );
    },
    [memberId, updateMutation]
  );

  const startEditMeta = () => {
    if (!member) return;
    setMetaForm({
      interviewDate: member.interviewDate,
      interviewer: member.interviewer,
    });
    setEditingMeta(true);
  };

  const startEditNotes = () => {
    if (!member) return;
    setNotesForm(member.notes);
    setEditingNotes(true);
  };

  const startEditAiInsights = () => {
    if (!member) return;
    setAiInsightsForm(member.aiInsights);
    setEditingAiInsights(true);
  };

  const startEditResponse = (idx: number) => {
    if (!member) return;
    setResponseForm({ ...member.interviewResponses[idx] });
    setEditingResponseIdx(idx);
  };

  const saveResponse = (idx: number) => {
    if (!member) return;
    const updated = [...member.interviewResponses];
    updated[idx] = { ...responseForm };
    save({ interviewResponses: updated });
  };

  const deleteResponse = (idx: number) => {
    if (!member) return;
    if (!window.confirm(`Delete Q${idx + 1}? This cannot be undone.`)) return;
    const updated = member.interviewResponses.filter((_, i) => i !== idx);
    save({ interviewResponses: updated });
  };

  const saveNewResponse = () => {
    if (!member || !newResponse.question.trim()) return;
    const updated = [...member.interviewResponses, { ...newResponse }];
    save({ interviewResponses: updated });
    setNewResponse({ question: "", answer: "" });
  };

  // ── Render ──

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/internal-interviews")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <p className="text-destructive">
          {error ? "Failed to load member details." : "Member not found."}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      {/* ── Back button ── */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/internal-interviews")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Interviews
      </Button>

      {/* ── Header ── */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{member.name}</h1>
        <p className="text-muted-foreground">
          {member.role || "No role"} · {member.team}
        </p>
      </div>

      {/* ── Member Info Card ── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Member Info
        </h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">Email</dt>
            <dd className="text-sm font-medium">{member.email || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">WhatsApp</dt>
            <dd className="text-sm font-medium">{member.whatsApp || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Status</dt>
            <dd>
              {member.status ? (
                <Badge variant={STATUS_VARIANTS[member.status] ?? "outline"}>
                  {member.status}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Reward Progress</dt>
            <dd className="text-sm font-medium">
              {member.rewardGiven === "0/3" ? "—" : member.rewardGiven}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Added</dt>
            <dd className="text-sm font-medium">
              {formatDate(member.createdAt)}
            </dd>
          </div>
        </dl>
      </section>

      {/* ── Interview Metadata ── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Interview Details
          </h2>
          {!editingMeta && (
            <button
              onClick={startEditMeta}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              aria-label="Edit interview details"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>

        {editingMeta ? (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Interview Date
              </label>
              <input
                type="date"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={metaForm.interviewDate}
                onChange={(e) =>
                  setMetaForm((f) => ({
                    ...f,
                    interviewDate: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Interviewer
              </label>
              <input
                type="text"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={metaForm.interviewer}
                onChange={(e) =>
                  setMetaForm((f) => ({ ...f, interviewer: e.target.value }))
                }
                placeholder="Name of interviewer"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() =>
                  save({
                    interviewDate: metaForm.interviewDate,
                    interviewer: metaForm.interviewer,
                  })
                }
                disabled={updateMutation.isPending}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingMeta(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-muted-foreground">Date Conducted</dt>
              <dd className="text-sm font-medium">
                {member.interviewDate
                  ? formatDate(member.interviewDate)
                  : "Not set"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Interviewer</dt>
              <dd className="text-sm font-medium">
                {member.interviewer || "Not set"}
              </dd>
            </div>
          </dl>
        )}
      </section>

      {/* ── Interview Responses ── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Interview Responses
            {member.interviewResponses.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                ({member.interviewResponses.length})
              </span>
            )}
          </h2>
          {!addingResponse && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setNewResponse({ question: "", answer: "" });
                setAddingResponse(true);
              }}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Q&A
            </Button>
          )}
        </div>

        {member.interviewResponses.length === 0 && !addingResponse && (
          <p className="text-sm text-muted-foreground">
            No interview responses recorded yet. Click "Add Q&A" to start
            documenting.
          </p>
        )}

        <div className="space-y-4">
          {member.interviewResponses.map(
            (resp: InterviewResponse, idx: number) => (
              <div
                key={idx}
                className="rounded-md border border-border bg-background p-4"
              >
                {editingResponseIdx === idx ? (
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Question
                      </label>
                      <input
                        type="text"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={responseForm.question}
                        onChange={(e) =>
                          setResponseForm((f) => ({
                            ...f,
                            question: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-muted-foreground">
                        Answer
                      </label>
                      <textarea
                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                        rows={4}
                        value={responseForm.answer}
                        onChange={(e) =>
                          setResponseForm((f) => ({
                            ...f,
                            answer: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveResponse(idx)}
                        disabled={updateMutation.isPending}
                      >
                        <Save className="mr-1.5 h-3.5 w-3.5" />
                        Save
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingResponseIdx(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold">
                        <span className="mr-1.5 text-muted-foreground">
                          Q{idx + 1}.
                        </span>
                        {resp.question}
                      </p>
                      <div className="flex shrink-0 gap-1">
                        <button
                          onClick={() => startEditResponse(idx)}
                          className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                          aria-label="Edit response"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteResponse(idx)}
                          className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                          aria-label="Delete response"
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {resp.answer || "No answer recorded."}
                    </p>
                  </>
                )}
              </div>
            )
          )}

          {/* ── Add new response form ── */}
          {addingResponse && (
            <div className="rounded-md border-2 border-dashed border-border bg-background p-4">
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Question
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newResponse.question}
                    onChange={(e) =>
                      setNewResponse((f) => ({
                        ...f,
                        question: e.target.value,
                      }))
                    }
                    placeholder="Enter the interview question"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Answer
                  </label>
                  <textarea
                    className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
                    rows={4}
                    value={newResponse.answer}
                    onChange={(e) =>
                      setNewResponse((f) => ({
                        ...f,
                        answer: e.target.value,
                      }))
                    }
                    placeholder="Enter the response"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={saveNewResponse}
                    disabled={
                      updateMutation.isPending || !newResponse.question.trim()
                    }
                  >
                    <Save className="mr-1.5 h-3.5 w-3.5" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAddingResponse(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Researcher's Summary ── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Researcher's Summary
          </h2>
          {!editingNotes && (
            <button
              onClick={startEditNotes}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              aria-label="Edit researcher's summary"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>

        {editingNotes ? (
          <div className="space-y-3">
            <textarea
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={6}
              maxLength={5000}
              value={notesForm}
              onChange={(e) => setNotesForm(e.target.value)}
              placeholder="Add researcher's summary or observations..."
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => save({ notes: notesForm })}
                disabled={updateMutation.isPending}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingNotes(false)}
              >
                Cancel
              </Button>
              <span className="ml-auto text-xs text-muted-foreground">
                {notesForm.length}/5000
              </span>
            </div>
          </div>
        ) : (
          <p
            className={cn(
              "whitespace-pre-wrap text-sm",
              member.notes ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {member.notes || "No summary yet."}
          </p>
        )}
      </section>

      {/* ── AI Insights ── */}
      <section className="rounded-lg border border-border bg-card p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            AI Insights
          </h2>
          {!editingAiInsights && (
            <button
              onClick={startEditAiInsights}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
              aria-label="Edit AI insights"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>

        {editingAiInsights ? (
          <div className="space-y-3">
            <textarea
              className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm"
              rows={6}
              maxLength={5000}
              value={aiInsightsForm}
              onChange={(e) => setAiInsightsForm(e.target.value)}
              placeholder="Add AI-generated insights from this interview..."
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => save({ aiInsights: aiInsightsForm })}
                disabled={updateMutation.isPending}
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingAiInsights(false)}
              >
                Cancel
              </Button>
              <span className="ml-auto text-xs text-muted-foreground">
                {aiInsightsForm.length}/5000
              </span>
            </div>
          </div>
        ) : (
          <p
            className={cn(
              "whitespace-pre-wrap text-sm",
              member.aiInsights ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {member.aiInsights || "No AI insights yet."}
          </p>
        )}
      </section>
    </div>
  );
}
